import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
  useMemo,
} from 'react';
import type { GlobalRole } from '@aica/core/rbac';

/**
 * ViewAsContext - Owner Mode functionality
 *
 * Allows platform owner to view UI as different roles without losing owner privileges.
 * This is a testing/preview feature for platform administrators only.
 *
 * When viewAsRole is set:
 * - UI adapts to show what that role would see
 * - Owner permissions remain active in backend
 * - Clear visual indicator shows active "View As" mode
 */

interface ViewAsContextValue {
  /** Current role being viewed as (null = viewing as actual role) */
  viewAsRole: GlobalRole | null;

  /** Enable "View As" mode with specific role */
  setViewAsRole: (role: GlobalRole | null) => void;

  /** Check if "View As" mode is active */
  isViewAsMode: boolean;

  /** Get the effective role for UI rendering (viewAsRole || actualRole) */
  getEffectiveRole: (actualRole: GlobalRole | null) => GlobalRole | null;
}

const ViewAsContext = createContext<ViewAsContextValue | null>(null);

export function ViewAsProvider({ children }: { children: ReactNode }) {
  const [viewAsRole, setViewAsRole] = useState<GlobalRole | null>(null);

  const isViewAsMode = viewAsRole !== null;

  const getEffectiveRole = useCallback(
    (actualRole: GlobalRole | null): GlobalRole | null => {
      return viewAsRole || actualRole;
    },
    [viewAsRole]
  );

  const value = useMemo(
    () => ({
      viewAsRole,
      setViewAsRole,
      isViewAsMode,
      getEffectiveRole,
    }),
    [viewAsRole, isViewAsMode, getEffectiveRole]
  );

  return <ViewAsContext.Provider value={value}>{children}</ViewAsContext.Provider>;
}

export const useViewAs = () => {
  const context = useContext(ViewAsContext);
  if (!context) throw new Error('useViewAs must be used within ViewAsProvider');
  return context;
};
